import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../stores/uiStore';
import { useFileStore } from '../../stores/fileStore';
import { foldersApi } from '../../api/folders';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toaster';
import { FOLDER_COLORS } from '@affogato/shared';
import clsx from 'clsx';

export function CreateFolderModal() {
  const { t } = useTranslation('files');
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isOpen = useUIStore((s) => s.isCreateFolderModalOpen);
  const closeModal = useUIStore((s) => s.closeCreateFolderModal);
  const currentFolderId = useFileStore((s) => s.currentFolderId);
  const currentProjectId = useFileStore((s) => s.currentProjectId);
  const currentClientId = useFileStore((s) => s.currentClientId);
  const addFolder = useFileStore((s) => s.addFolder);
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsLoading(true);
    try {
      const { folder } = await foldersApi.create({
        name: name.trim(),
        parentId: currentFolderId,
        color: color || undefined,
        projectId: currentProjectId || undefined,
        clientId: currentClientId || undefined,
      });

      addFolder(folder);
      toast.success(t('toast.folderCreated'), t('toast.folderCreatedDesc', { name: folder.name }));
      handleClose();
    } catch (error) {
      toast.error(t('toast.folderCreateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setColor(null);
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('createFolder.title')}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('createFolder.folderName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('createFolder.placeholder')}
          autoFocus
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            {t('createFolder.colorOptional')}
          </label>
          <div className="flex flex-wrap gap-2">
            {FOLDER_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(color === c ? null : c)}
                className={clsx(
                  'w-8 h-8 rounded-full transition-all hover:scale-110 active:scale-95',
                  color === c && 'ring-2 ring-offset-2'
                )}
                style={{
                  backgroundColor: c,
                  // @ts-expect-error - Custom CSS property for ring color
                  '--tw-ring-color': c,
                }}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="flex-1"
          >
            {t('common:buttons.cancel')}
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={!name.trim()}
            className="flex-1"
          >
            {t('common:buttons.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
